import { cloneDeep, compact, first, isNil, mean, minBy, sumBy } from 'lodash';
import { DataCollectionMode } from '../enums/data-collector.enums';
import { BallType } from '../enums/machine.enums';
import { BuildPriority } from '../enums/pitches.enums';
import {
  DELTA_BREAKS_INCHES_FOR_3_6OZ,
  MAX_TRAINING_SHOTS,
  MIN_TRAINING_SHOTS,
  PRESET_TRAINING_OPTIONS,
  PresetTrainingMode,
  TrainingOutcome,
} from '../enums/training.enums';
import { Nullable } from '../interfaces/common/i-generics';
import { IMachineState } from '../interfaces/i-machine-state';
import { ITrajectory } from '../interfaces/pitches';
import { IPosition } from '../interfaces/pitches/i-base';
import {
  IContShotError,
  IContShotErrorBreak,
  IContShotErrorPlate,
  IContShotErrorSpeed,
} from '../interfaces/training/continuous-training/i-shot-error';
import { IMachineShot } from '../interfaces/training/i-machine-shot';
import { IPresetOption } from '../interfaces/training/i-preset-training-spec';
import { ITrainedMachineState } from '../interfaces/training/i-trained-machine-state';
import {
  IItrLog,
  ITrainingLog,
  ITrainingLogSummary,
} from '../interfaces/training/i-training-log';
import { ArrayHelper } from './array.helper';
import { EllipseHelper } from './ellipse.helper';
import { FT_TO_INCHES, getMagnitude, meanObject } from './math.utilities';
import { TrajHelper } from './trajectory.helper';

const ESTIMATE_TIME_PER_SHOT_SEC = 22;

const PT_DEFINITION = PRESET_TRAINING_OPTIONS.find(
  (o) => o.mode === PresetTrainingMode.Precision
) as IPresetOption;

export class TrainingHelper {
  static getTrainingThresholdOptions = (currentValue: Nullable<number>) => {
    const output = ArrayHelper.getIntegerOptions(
      MIN_TRAINING_SHOTS,
      MAX_TRAINING_SHOTS,
      {
        // ensure the current value (if any) is included
        includeLabels: isNil(currentValue) ? undefined : [currentValue],
      }
    );

    return output;
  };

  static getDataCollectorShots(mode: DataCollectionMode): {
    aiming: number;
    analysis: number;
  } {
    switch (mode) {
      case 'BTC': {
        return {
          aiming: 3,
          analysis: 0,
        };
      }

      case 'RT': {
        return {
          aiming: 3,
          // faster for local testing via mocks
          analysis: 25,
        };
      }

      case 'HWD': {
        return {
          // * note that the 3 from RT will be loaded instead
          aiming: 3,
          analysis: 6,
        };
      }

      default: {
        return {
          aiming: 0,
          analysis: 0,
        };
      }
    }
  }

  /** by default returns results in seconds */
  static getETAForShots = (remaining: number, unit: 'sec' | 'min') => {
    const seconds = ESTIMATE_TIME_PER_SHOT_SEC * remaining;

    if (unit === 'min') {
      return Math.ceil(seconds / 60);
    }

    return seconds;
  };

  static getIterationPtRelThreshold = (config: {
    priority: BuildPriority;
    ball_type: BallType;
    itr: IItrLog;
  }) => {
    const { priority, ball_type, itr } = config;

    const deltas: number[] = [
      itr.preset.spec.deltaSpeedMPH / PT_DEFINITION.spec.deltaSpeedMPH,
    ];

    switch (priority) {
      case BuildPriority.Breaks: {
        const ballSpecificDefault =
          ball_type === BallType.Trajekt_36
            ? DELTA_BREAKS_INCHES_FOR_3_6OZ
            : PT_DEFINITION.spec.deltaBreaksInches;

        deltas.push(itr.preset.spec.deltaBreaksInches / ballSpecificDefault);
        break;
      }

      case BuildPriority.Spins: {
        deltas.push(
          itr.preset.spec.deltaSpinsRPM / PT_DEFINITION.spec.deltaSpinsRPM
        );
        break;
      }

      default: {
        break;
      }
    }

    return mean(deltas);
  };

  private static didAttemptPT = (log: ITrainingLog) => {
    if (log.precisionTrained) {
      // completed successfully
      return true;
    }

    if (log.iterations.length > 1) {
      // we iterated
      return true;
    }

    // everything else (no iteration, no success)
    return false;
  };

  static getLogOutcome = (log: ITrainingLog): TrainingOutcome => {
    if (this.didAttemptPT(log)) {
      // tried PT, gave up before completing
      if (!log.completed) {
        return TrainingOutcome.PT_GiveUp;
      }

      // tried PT, completed as PT
      if (log.precisionTrained) {
        return TrainingOutcome.PT_Complete;
      }

      // tried PT, completed as QT
      return TrainingOutcome.PT_Failure;
    }

    // did not try PT, gave up before completing
    if (!log.completed) {
      return TrainingOutcome.QT_GiveUp;
    }

    // did not try PT, completed as QT
    return TrainingOutcome.QT_Complete;
  };

  static summarizeLogs = (logs: ITrainingLog[]): ITrainingLogSummary => {
    const ptAttemptedLogs = logs.filter((log) => this.didAttemptPT(log));

    const ptSucceededLogs = ptAttemptedLogs.filter(
      (log) => log.precisionTrained
    );

    const firstIterations: IItrLog[] = [];
    const breakIterations: IItrLog[] = [];
    const spinIterations: IItrLog[] = [];

    logs.forEach((log) => {
      const firstItr = minBy(log.iterations, (m) => m.index);

      if (!firstItr) {
        return;
      }

      firstIterations.push(firstItr);

      if (log.pitchPriority === BuildPriority.Breaks) {
        breakIterations.push(firstItr);
      }

      if (log.pitchPriority === BuildPriority.Spins) {
        spinIterations.push(firstItr);
      }
    });

    const ptAttemptedIterations = sumBy(
      ptAttemptedLogs,
      (log) => log.iterations.length
    );

    const output: ITrainingLogSummary = {
      machineID: first(logs)?.machineID ?? '???',

      ptRate: logs.length === 0 ? 0 : ptAttemptedLogs.length / logs.length,
      ptSuccessRate:
        ptAttemptedLogs.length === 0
          ? 0 // avoid divide by 0
          : ptSucceededLogs.length / ptAttemptedLogs.length,
      ptSmartSamplingRate:
        ptAttemptedIterations === 0
          ? 0
          : sumBy(
              ptAttemptedLogs,
              (log) =>
                log.iterations.filter((itr) => itr.preset.spec.smartSampling)
                  .length
            ) / ptAttemptedIterations,

      meanPtRelThresholds: mean(
        ptAttemptedLogs.map((log) => {
          const itrThresholds = log.iterations.map((itr) =>
            this.getIterationPtRelThreshold({
              priority: log.pitchPriority,
              ball_type: log.ball_type,
              itr: itr,
            })
          );

          return mean(itrThresholds);
        })
      ),

      meanPtTrainingThreshold: mean(
        ptAttemptedLogs.map((log) => log.training_threshold)
      ),
      meanPtSuccessIterations: mean(
        ptSucceededLogs.map((log) => log.iterations.length)
      ),
      meanPtSuccessShots: mean(
        ptSucceededLogs.map((log) => {
          const totalShots = sumBy(log.iterations, (itr) => itr.shotIDs.length);
          return totalShots;
        })
      ),
      meanPtSuccessExcessShots: mean(
        ptSucceededLogs.map((log) => {
          const totalShots = sumBy(log.iterations, (itr) => itr.shotIDs.length);
          return Math.max(0, totalShots - log.training_threshold);
        })
      ),

      maeVMph: mean(firstIterations.map((itr) => Math.abs(itr.meanError.vMph))),

      maePlateXIn: mean(
        firstIterations.map((itr) => Math.abs(itr.meanError.plateXIn))
      ),
      maePlateZIn: mean(
        firstIterations.map((itr) => Math.abs(itr.meanError.plateZIn))
      ),

      maeWXRpm: mean(
        spinIterations
          .map((itr) => itr.meanError.wxRpm)
          .filter((n) => n !== null)
          .map((n) => Math.abs(n as number))
      ),
      maeWYRpm: mean(
        spinIterations
          .map((itr) => itr.meanError.wyRpm)
          .filter((n) => n !== null)
          .map((n) => Math.abs(n as number))
      ),
      maeWZRpm: mean(
        spinIterations
          .map((itr) => itr.meanError.wzRpm)
          .filter((n) => n !== null)
          .map((n) => Math.abs(n as number))
      ),

      maeBreakXIn: mean(
        breakIterations
          .map((itr) => itr.meanError.breakXIn)
          .filter((n) => n !== null)
          .map((n) => Math.abs(n as number))
      ),
      maeBreakZIn: mean(
        breakIterations
          .map((itr) => itr.meanError.breakZIn)
          .filter((n) => n !== null)
          .map((n) => Math.abs(n as number))
      ),
    };

    return output;
  };

  static getStdErrMeanBreaks = (breakErrors: IContShotErrorBreak[]): number => {
    const sampleVariance = (values: number[]): number => {
      if (values.length < 2) {
        // shouldn't trigger
        return NaN;
      }

      const avg = mean(values);

      return (
        values.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) /
        (values.length - 1)
      );
    };

    const varX = sampleVariance(breakErrors.map((e) => e.break_x_in));
    const varZ = sampleVariance(breakErrors.map((e) => e.break_z_in));
    const rmsStd = Math.sqrt((varX + varZ) / 2);
    return rmsStd / Math.sqrt(breakErrors.length);
  };

  static getUncertaintyErrorRatio = (
    uncertainty_in_average: number,
    mean_error_magnitude: number,
    threshold: number,
    eps = 1e-6
  ): number => {
    return (
      uncertainty_in_average / Math.max(mean_error_magnitude - threshold, eps)
    );
  };

  // ? lighter balls > require more error samples before adjusting (b/c we expect more variance)
  // ? baden balls > require fewer error samples before adjusting (b/c we expect less variance)
  static getMinSamplesMsForContTraining = (ball_type: BallType) => {
    switch (ball_type) {
      // dimpled 5oz
      case BallType.BadenDimpledSeams: {
        return 3;
      }

      // standard light
      case BallType.Marv:
      case BallType.Trajekt_36:
      case BallType.Trajekt_42:
      case BallType.StingFreeJugs: {
        return 7;
      }

      // standard 5oz
      default: {
        return 3;
      }
    }
  };

  static shouldAdjustForContTraining = (tms: ITrainedMachineState): boolean => {
    // don't adjust for LTE 3 error samples
    const MIN_SAMPLES_AIM = 3;
    // when samples is fewer than max, check uncertainty
    const MAX_UNCERTAINTY_TO_ERROR_RATIO = 0.5;

    // invoke an adjustment b/c of plate if errors exceed the threshold
    function forPlates(plateErrors: IContShotErrorPlate[]): boolean {
      const PLATE_THRESHOLD_IN = 1;

      if (plateErrors.length < MIN_SAMPLES_AIM) {
        // filtered errors might be fewer than the total number
        return false;
      }

      // todo: maybe a weighted mean to favor recent shots
      const meanError = meanObject(plateErrors);

      if (!meanError) {
        return false;
      }

      return (
        getMagnitude([meanError.plate_x_in, meanError.plate_z_in]) >
        PLATE_THRESHOLD_IN
      );
    }

    function forBreaks(breakErrors: IContShotErrorBreak[]): boolean {
      const BREAK_THRESHOLD_IN = 1.5;

      const countSamples = breakErrors.length;

      if (
        countSamples <
        TrainingHelper.getMinSamplesMsForContTraining(tms.ball_type)
      ) {
        // don't adjust for LT X error samples
        return false;
      }

      const meanError = meanObject(breakErrors);

      if (!meanError) {
        return false;
      }

      const meanErrMag = getMagnitude([
        meanError.break_x_in,
        meanError.break_z_in,
      ]);

      if (meanErrMag <= BREAK_THRESHOLD_IN) {
        return false;
      }

      // check if uncertainty/error ratio is below threshold

      // standard error of the mean
      const stdErrMean = TrainingHelper.getStdErrMeanBreaks(breakErrors);

      // uncertainty-to-error ratio
      const uncertaintyRatio = TrainingHelper.getUncertaintyErrorRatio(
        stdErrMean,
        meanErrMag,
        BREAK_THRESHOLD_IN
      );

      // adjust if uncertainty is low enough
      return uncertaintyRatio <= MAX_UNCERTAINTY_TO_ERROR_RATIO;
    }

    function forSpeed(speedErrors: IContShotErrorSpeed[]): boolean {
      const SPEED_THRESHOLD_MPH = 1.4;

      const countSamples = speedErrors.length;

      if (
        countSamples <
        TrainingHelper.getMinSamplesMsForContTraining(tms.ball_type)
      ) {
        // don't adjust for LT X error samples
        return false;
      }

      const meanError = meanObject(speedErrors);

      if (!meanError) {
        return false;
      }

      if (meanError.speed_mph <= SPEED_THRESHOLD_MPH) {
        return false;
      }

      // check if uncertainty/error ratio is below threshold

      // standard deviation of the sample
      const sampleStd = Math.sqrt(
        speedErrors
          .map((m) => m.speed_mph)
          .map((x) => Math.pow(x - meanError.speed_mph, 2))
          .reduce((a, b) => a + b) /
          (countSamples - 1)
      );

      // standard error of the mean
      const stdErrMean = sampleStd / Math.sqrt(countSamples);

      // uncertainty-to-error ratio
      const uncertaintyRatio = TrainingHelper.getUncertaintyErrorRatio(
        stdErrMean,
        meanError.speed_mph,
        SPEED_THRESHOLD_MPH
      );

      // adjust if uncertainty is low enough
      return uncertaintyRatio <= MAX_UNCERTAINTY_TO_ERROR_RATIO;
    }

    try {
      if (!tms.contShotErrors) {
        return false;
      }

      if (tms.contShotErrors.length < MIN_SAMPLES_AIM) {
        return false;
      }

      // todo: check interval ???

      if (forPlates(tms.contShotErrors.filter((m) => !m.ignoreForPlate))) {
        return true;
      }

      if (forBreaks(tms.contShotErrors)) {
        return true;
      }

      if (forSpeed(tms.contShotErrors)) {
        return true;
      }

      return false;
    } catch (e) {
      console.error('shouldAdjustForContTraining', tms, e);
      return false;
    }
  };

  /**
   * during BP, new shots are added to the TMS and contShotErrors is populated with errors from the training shots
   * then run a shouldAdjustForContTraining on everything in contShotErrors
   * if necessary, call TDS continuous training endpoint
   * if/when TMS is returned to server, server will write it to the TMS collection before passing back to app
   * - ms
   * - shotIDs
   * - contShotErrors
   * - last_adjustments
   * - aimingTraj
   * app will merge to TMS store dictionary
   */

  static getContShotError = (
    shot: IMachineShot
  ): IContShotError | undefined => {
    const FN_NAME = 'getContShotError';

    try {
      const {
        _id,
        _created,
        bi_timeout,
        actual_ms,
        confidence,
        break: breakConfidence,
        traj,
        target_traj,
        training,
      } = shot;

      if (!target_traj) {
        throw new Error('target_traj is required');
      }

      // if not training, check if BI timeout happened
      // todo: determine which bi_timeout is most accurate, remove the other one
      if (!training && (bi_timeout || actual_ms.bi_timeout)) {
        return undefined;
      }

      // if not training, check confidence exists
      if (!training && !confidence) {
        return undefined;
      }

      // if not training, check root confidence is good
      if (
        !training &&
        confidence &&
        [
          confidence.PITCH_SpeedConfidence,
          // confidence.PITCH_SpinConfidence,
          confidence.PITCH_StrikePositionHeightConfidence,
          confidence.PITCH_StrikePositionSideConfidence,
        ].some((c) => c !== 1)
      ) {
        return undefined;
      }

      // if not training, check break confidence exists
      if (!training && !breakConfidence) {
        return undefined;
      }

      // if not training, check break confidence is good
      if (
        !training &&
        breakConfidence &&
        [
          // breakConfidence.PITCH_HBSpinConfidence,
          breakConfidence.PITCH_HBTrajectoryConfidence,
          // breakConfidence.PITCH_VBSpinConfidence,
          breakConfidence.PITCH_VBTrajectoryConfidence,
        ].some((c) => c !== 1)
      ) {
        return undefined;
      }

      const plateFt = TrajHelper.getPlateLoc(traj);
      const targetPlateFt = TrajHelper.getPlateLoc(target_traj);

      const breaksIn = TrajHelper.getBreaks(traj);
      const targetBreaksIn = TrajHelper.getBreaks(target_traj);

      const output: IContShotError = {
        _id: _id,
        _created: _created,
        training: training,

        plate_x_in: (plateFt.plate_x - targetPlateFt.plate_x) * FT_TO_INCHES,
        plate_z_in: (plateFt.plate_z - targetPlateFt.plate_z) * FT_TO_INCHES,

        break_x_in: breaksIn.xInches - targetBreaksIn.xInches,
        break_z_in: breaksIn.zInches - targetBreaksIn.zInches,

        // todo: these need to be populated and used later when spins are detected
        spin_x_rpm: -1,
        spin_y_rpm: -1,
        spin_z_rpm: -1,

        speed_mph:
          TrajHelper.getSpeedFromTraj(traj) -
          TrajHelper.getSpeedFromTraj(target_traj),
      };

      return output;
    } catch (e) {
      console.error(`${FN_NAME}: failed to get cont shot error`, shot, e);
      return undefined;
    }
  };

  // purpose: fix target_traj of each shot to be comparable to the traj > errors can be calculated fairly
  static alignTrainingShots = (
    // from TMS
    ms: IMachineState,
    // should be equal to py from ms
    releaseFt: number,
    // from TMS
    aimingTraj: ITrajectory,
    shots: IMachineShot[]
  ): IMachineShot[] => {
    const inputs = shots.map((shot) => cloneDeep(shot));

    // align each shot to the ms tilt + yaw
    const rotated = EllipseHelper.getRotatedShots({
      ms: ms,
      shots: inputs,
      releaseFt: releaseFt,
    });

    // rotate each shot to the plate location of the aimingTraj
    const release: IPosition = {
      px: ms.px,
      py: releaseFt,
      pz: ms.pz,
    };

    const plate = TrajHelper.getPlateLoc(aimingTraj);

    const aimed = compact(
      rotated.map((shot) => {
        if (!shot.target_traj) {
          // shouldn't trigger
          return undefined;
        }

        // update the target_traj
        shot.target_traj = TrajHelper.aimTrajectory(
          shot.target_traj,
          release,
          plate
        );

        return shot;
      })
    );

    return aimed;
  };
}
